import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Create axios instance with base configuration
const axiosService: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
axiosService.interceptors.request.use(
  (config) => {
    // Log the request for debugging
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosService.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses
    console.log(`Response received from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url
      });
      
      // Handle specific status codes
      switch (error.response.status) {
        case 401:
          console.error('Unauthorized access - token may be expired');
          break;
        case 403:
          console.error('Forbidden - insufficient permissions');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Internal server error');
          break;
        default:
          console.error('API request failed');
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error - no response received:', error.request);
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Error message constants
export const AUTH_ERRORS = {
  NO_ACCESS_TOKEN: 'Session Expired. PLease log in.'
};

// Simple retry mechanism
const retryRequest = async <T = any>(
  requestFn: () => Promise<AxiosResponse<T>>,
  maxRetries: number = 3
): Promise<AxiosResponse<T>> => {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on authentication errors (401, 403) or client errors (4xx)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }
      
      // Log retry attempt
      if (attempt < maxRetries) {
        console.warn(`Request failed (attempt ${attempt}/${maxRetries}), retrying...`, error.message);
        // Simple delay between retries (500ms * attempt number)
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
      }
    }
  }
  
  // If all retries failed, throw the last error
  console.error(`Request failed after ${maxRetries} attempts`);
  throw lastError;
};

// Helper function to make authenticated requests
export const makeAuthenticatedRequest = async <T = any>(
  config: AxiosRequestConfig,
  accessToken?: string
): Promise<AxiosResponse<T>> => {
  // Check if access token is available for authenticated requests
  if (!accessToken) {
    throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
  }
  
  // Add authorization header
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${accessToken}`,
  };
  
  // Use retry mechanism for the request
  return retryRequest(() => axiosService.request<T>(config));
};
// Function to bind users
export const bindUsers = async (
  applicationName: string,
  accountId: number,
  accessToken: string
): Promise<AxiosResponse<any>> => {
  const url = `admin/get-users-by-account?applicationName=${applicationName}&accountId=${accountId}`;
  return makeAuthenticatedRequest({ method: 'GET', url }, accessToken);
};

// API service methods
export const apiService = {
  // GET request
  get: async <T = any>(url: string, accessToken?: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return makeAuthenticatedRequest<T>({ ...config, method: 'GET', url }, accessToken);
  },

  // POST request
  post: async <T = any>(url: string, data?: any, accessToken?: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return makeAuthenticatedRequest<T>({ ...config, method: 'POST', url, data }, accessToken);
  },

  // PUT request
  put: async <T = any>(url: string, data?: any, accessToken?: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return makeAuthenticatedRequest<T>({ ...config, method: 'PUT', url, data }, accessToken);
  },

  // DELETE request
  delete: async <T = any>(url: string, accessToken?: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return makeAuthenticatedRequest<T>({ ...config, method: 'DELETE', url }, accessToken);
  },

  // PATCH request
  patch: async <T = any>(url: string, data?: any, accessToken?: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return makeAuthenticatedRequest<T>({ ...config, method: 'PATCH', url, data }, accessToken);
  },
};

// User profile specific API calls
export interface UserProfile {
  firstName: string;
  lastName: string;
  email?: string;
  // Add other profile fields as needed
}

export interface UserRole {
  userId: string;
  role: string;
  isAdmin: boolean;
  adminApplications: AdminPrivilegeDetailsModel[];
}

export interface AdminPrivilegeDetailsModel {
  accountId:Number,
  applicationName: string;
  hasAdminPrivileges: boolean[];
}

export const userProfileService = {
  //Get User Role
  getUserRole: async (accessToken: string): Promise<UserRole> => {
    try {
      const response = await apiService.get<UserRole>('/user/get-user-role', accessToken);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user role:', error);
      throw error;
    }
  },

  // Get user profile
  getUserProfile: async (accessToken: string): Promise<UserProfile> => {
    try {
      const response = await apiService.get<UserProfile>('/user/profile', accessToken);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (accessToken: string, profileData: Partial<UserProfile>): Promise<UserProfile> => {
    try {
      const response = await apiService.put<UserProfile>('/user/profile', profileData, accessToken);
      return response.data;
    } catch (error) {
      console.error('Failed to update user profile:', error);
      throw error;
    }
  },
};

// User list interface
export interface User {
  id: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  jobFunction: string;
  status: 'Active' | 'Deactivated' | 'Pending';
  oktaUserId?: string;
  // Additional fields for Protect application
  productAccess?: string[] | string;
  role?: string;
  location?: string;
}

// Job function interface
export interface JobFunction {
  jobFunctionId: string;
  jobFunction: string;
}

// User list service
export const userListService = {
  // Get all users
  getAllUsers: async (accessToken: string, account: any): Promise<User[]> => {
    try {
        // Call bindUsers to fetch the user list
        const accountId = account ? account.accountId : '';
        const applicationName = account ? account.applicationName : '';
        const response = await bindUsers(applicationName, accountId, accessToken);
        const userData = response.data.map((apiUser: any): User => ({
                    id: apiUser.userId,
                    userName: apiUser.userName,
                    email: apiUser.email,
                    firstName: apiUser.firstName || '',
                    lastName: apiUser.lastName || '',
                    jobFunction: apiUser.jobFunction ?? '',
                    status: apiUser.status == 'Active' ? null: apiUser.status,
                    oktaUserId: apiUser.oktaUserId,
                    // Don't fetch productAccess here as it's expensive
                    // Use empty string instead of null to avoid TypeScript errors
                    productAccess: '',
                    //protect specific fields
                    location: apiUser.protectLocation,
                    role: apiUser.protectAccountRoleGroupName
               }));
        return userData;
    } catch (error) {
        console.error('Failed to fetch users list:', error);
      throw error;
    }
  },

  // Get user by ID for editing
  getUserById: async (accessToken: string, userId: string): Promise<any> => {
    try {
      console.log(`Fetching user details for userId: ${userId}`);
      const response = await apiService.get<any>(`cum-users/get-user?userid=${userId}`, accessToken);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch user details for userId ${userId}:`, error);
      throw error;
    }
  },
  
  // Get product access for a specific user
  getUserProductAccess: async (accessToken: string, oktaUserId: string): Promise<string[]> => {
    try {
      console.log(`Fetching product access for user: ${oktaUserId}`);
      
      // Call the actual API endpoint
      const response = await apiService.get<any>(`admin/user-applications?userId=${oktaUserId}`, accessToken);
      
      // Extract applications from the response
      if (response.data && Array.isArray(response.data.applications)) {
        return response.data.applications;
      }
      
      // Return empty array if no applications found
      return [];
    } catch (error) {
      console.error(`Failed to fetch product access for user ${oktaUserId}:`, error);
      
      // In case of error, return empty array
      return [];
    }
  }
};

// Job function service
export const jobFunctionService = {
  // Get all job functions
  getAllJobFunctions: async (accessToken: string): Promise<JobFunction[]> => {
    try {
      const response = await apiService.get<JobFunction[]>('jobfunction/all', accessToken);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch job functions:', error);
      
      // Fallback to mock data if API fails
      const { mockJobFunctions } = await import('../data/mockUsers');
      return mockJobFunctions;
    }
  }
};

// Location tree interfaces
export interface LocationNode {
  locationId: number;
  locationNodeId: number;
  name: string;
  level: number;
  parentLocationId: number | null;
  childCount: number;
  hasChildren: boolean;
  isRecycleBin: boolean;
  children: LocationNode[];
}

export interface LocationTreeResponse {
  accountId: number;
  rootNodeName: string;
  locations: LocationNode[];
  totalCount: number;
}

// Location service
export const locationService = {
  // Get location tree by account
  getLocationTreeByAccount: async (accessToken: string, accountId: number): Promise<LocationTreeResponse> => {
    try {
      const response = await apiService.get<LocationTreeResponse>(`locations/get-location-tree-by-account?accountId=${accountId}`, accessToken);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch location tree:', error);
      // Return empty response if API fails
      return {
        accountId: accountId,
        rootNodeName: "All Locations",
        locations: [],
        totalCount: 0
      };
    }
  }
};

export interface UserGroup {
  id: number;
  name: string;
  description?: string;
}

// Get user groups for an account
export const getUserGroups = async (
  accountId: number,
  applicationName: string,
  accessToken?: string
): Promise<UserGroup[]> => {
  try {
    const response = await apiService.get<UserGroup[]>(
      `admin/account-usergroups?accountId=${accountId}&applicationName=${applicationName}`,
      accessToken
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch user groups:', error);
    throw error;
  }
};


// User creation request interface
export interface CreateUserRequest {
  FirstName: string;
  LastName: string;
  Email: string;
  Login: string;
  Password?: string | null;
  AccountRoleGroupId?: number | null;
  LocationIds: number[];
  UserNotes: string;
  IsFederatedUser: boolean;
  DefaultSearchResultsPerPage: number;
  NotificationEmail: string;
  ApplicationName: string;
  AccountId: number;
}

// Create user API call
export const createUser = async (
  user: CreateUserRequest,
  accessToken?: string
): Promise<any> => {
  try {
    const response = await apiService.post('/cum-users/Create', user, accessToken);
    return response.data;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
};

// Invite users interfaces
export interface InviteUsersRequest {
  Emails: string[];
  AccountRoleGroupId: number;
  LocationIds: number[];
  InviterContactInfo: string;
  ExpirationDays: number;
  ApplicationName: string;
  AccountId: number;
}

export interface FailedInvite {
  email: string;
  errorMessage: string;
  errorDetails: string;
}

export interface InviteUsersResponse {
  success: boolean;
  message: string;
  successfulInvites: string[];
  failedInvites: FailedInvite[];
  invalidEmails: string[];
  totalInvitationsSent: number;
  successCount: number;
  failedCount: number;
}

// Invite users service
export const inviteUsersService = {
  // Send invitations to users
  inviteUsers: async (
    inviteRequest: InviteUsersRequest,
    accessToken: string
  ): Promise<InviteUsersResponse> => {
    try {
      const response = await apiService.post<InviteUsersResponse>(
        '/cum-users/invite-users',
        inviteRequest,
        accessToken
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send invitations:', error);
      throw error;
    }
  }
};

export default axiosService;